import React, { useLayoutEffect, useRef, useState } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

const ChartRenderer = ({ config, enableClick = false }) => {
  const chartRef = useRef(null);
  const enlargedChartRef = useRef(null);
  const [isEnlarged, setIsEnlarged] = useState(false);

  useLayoutEffect(() => {
    let root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const { type, data, keys, title } = config;
    const { category, value } = keys;

    // --- GRÁFICA DE BARRAS / COLUMNAS / LINEAS ---
    if (type === 'bar' || type === 'column' || type === 'line') {
      let chart = root.container.children.push(am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "zoomX",
        pinchZoomX: true
      }));

      let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
      cursor.lineY.set("visible", false);

      let xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
      xRenderer.labels.template.setAll({ rotation: -45, centerY: am5.p50, centerX: am5.p100 });

      let xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
        maxDeviation: 0.3,
        categoryField: category,
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {})
      }));

      let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
        maxDeviation: 0.3,
        renderer: am5xy.AxisRendererY.new(root, {})
      }));

      let series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: "Series 1",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: value,
        sequencedInterpolation: true,
        categoryXField: category,
        tooltip: am5.Tooltip.new(root, { labelText: "{valueY}" })
      }));

      series.columns.template.setAll({ cornerRadiusTL: 5, cornerRadiusTR: 5 });
      series.columns.template.adapters.add("fill", function(fill, target) {
        return chart.get("colors").getIndex(series.columns.indexOf(target));
      });

      xAxis.data.setAll(data);
      series.data.setAll(data);
      series.appear(1000);
      chart.appear(1000, 100);
    }

    // --- GRÁFICA DE PASTEL (PIE) ---
    else if (type === 'pie') {
        let chart = root.container.children.push(am5percent.PieChart.new(root, {
            layout: root.verticalLayout
        }));

        let series = chart.series.push(am5percent.PieSeries.new(root, {
            valueField: value,
            categoryField: category
        }));

        series.data.setAll(data);
        series.appear(1000, 100);
    }

    return () => {
      root.dispose();
    };
  }, [config]);

  // Render enlarged chart in modal
  useLayoutEffect(() => {
    if (!isEnlarged || !enlargedChartRef.current) return;

    let root = am5.Root.new(enlargedChartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const { type, data, keys } = config;
    const { category, value } = keys;

    if (type === 'bar' || type === 'column' || type === 'line') {
      let chart = root.container.children.push(am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "zoomX",
        pinchZoomX: true
      }));

      let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
      cursor.lineY.set("visible", false);

      let xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
      xRenderer.labels.template.setAll({ rotation: -45, centerY: am5.p50, centerX: am5.p100 });

      let xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
        maxDeviation: 0.3,
        categoryField: category,
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {})
      }));

      let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
        maxDeviation: 0.3,
        renderer: am5xy.AxisRendererY.new(root, {})
      }));

      let series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: "Series 1",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: value,
        sequencedInterpolation: true,
        categoryXField: category,
        tooltip: am5.Tooltip.new(root, { labelText: "{valueY}" })
      }));

      series.columns.template.setAll({ cornerRadiusTL: 5, cornerRadiusTR: 5 });
      series.columns.template.adapters.add("fill", function(fill, target) {
        return chart.get("colors").getIndex(series.columns.indexOf(target));
      });

      xAxis.data.setAll(data);
      series.data.setAll(data);
      series.appear(1000);
      chart.appear(1000, 100);
    }
    else if (type === 'pie') {
        let chart = root.container.children.push(am5percent.PieChart.new(root, {
            layout: root.verticalLayout
        }));

        let series = chart.series.push(am5percent.PieSeries.new(root, {
            valueField: value,
            categoryField: category
        }));

        series.data.setAll(data);
        series.appear(1000, 100);
    }

    return () => {
      root.dispose();
    };
  }, [isEnlarged, config]);

  const handleChartClick = () => {
    if (enableClick) {
      setIsEnlarged(true);
    }
  };

  const handleCloseModal = () => {
    setIsEnlarged(false);
  };

  return (
    <>
      <div
        style={{
          width: "100%",
          height: "300px",
          marginTop: "10px",
          marginBottom: "10px",
          cursor: enableClick ? 'pointer' : 'default'
        }}
        onClick={handleChartClick}
      >
          {config.title && <div style={{textAlign:'center', fontSize:'12px', fontWeight:'bold', marginBottom:'5px'}}>{config.title}</div>}
          <div ref={chartRef} style={{ width: "100%", height: "100%" }}></div>
      </div>

      {/* Modal for enlarged chart */}
      {isEnlarged && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={handleCloseModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '20px',
              width: '90%',
              maxWidth: '1200px',
              height: '80%',
              maxHeight: '800px',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleCloseModal}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: '#ff5252',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: 'bold',
                zIndex: 10000
              }}
            >
              ✕
            </button>
            {config.title && (
              <div style={{textAlign:'center', fontSize:'18px', fontWeight:'bold', marginBottom:'15px'}}>
                {config.title}
              </div>
            )}
            <div ref={enlargedChartRef} style={{ width: "100%", height: "calc(100% - 40px)" }}></div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChartRenderer;
